import { getProductProfile } from './product.js';
import type { PhotobookProject, PageElement, PhotoFrameElement } from './schemas.js';

export type PreflightSeverity = 'info' | 'warning' | 'error' | 'blocking';

export type PreflightIssue = {
  id: string;
  code: string;
  severity: PreflightSeverity;
  message: string;
  pageId?: string;
  coverPanel?: 'front' | 'back' | 'spine';
  elementId?: string;
};

export type DpiInput = {
  sourceWidthPx: number;
  sourceHeightPx: number;
  frameWidthMm: number;
  frameHeightMm: number;
  placementScale: number;
  /** Visible crop fraction of source (1 = full cover without extra zoom) */
  cropScale?: number;
};

/**
 * Effective print DPI based on visible source pixels over physical frame size.
 * 25.4 mm = 1 inch.
 */
export function calculateEffectiveDpi(input: DpiInput): number {
  const cropScale = input.cropScale ?? 1;
  // Cover fit uses the smaller dimension ratio; extra placement.scale zooms further in
  const coverScale = Math.max(
    input.frameWidthMm / (input.sourceWidthPx || 1),
    input.frameHeightMm / (input.sourceHeightPx || 1),
  );
  // mm per source pixel after cover + user scale
  const mmPerPxX = coverScale / (input.placementScale * cropScale);
  const mmPerPxY = coverScale / (input.placementScale * cropScale);
  const dpiX = 25.4 / mmPerPxX;
  const dpiY = 25.4 / mmPerPxY;
  // More accurate: pixels across frame after crop
  const visiblePxX = input.frameWidthMm / mmPerPxX;
  const visiblePxY = input.frameHeightMm / mmPerPxY;
  const dpiFromX = visiblePxX / (input.frameWidthMm / 25.4);
  const dpiFromY = visiblePxY / (input.frameHeightMm / 25.4);
  void dpiX;
  void dpiY;
  return Math.min(dpiFromX, dpiFromY);
}

export function dpiStatus(
  dpi: number,
  profile: { targetDpi: number; warnDpi: number; minDpi: number },
): 'ok' | 'warn' | 'low' | 'critical' {
  if (dpi >= profile.targetDpi) return 'ok';
  if (dpi >= profile.warnDpi) return 'warn';
  if (dpi >= profile.minDpi) return 'low';
  return 'critical';
}

function collectElements(project: PhotobookProject): Array<{
  pageId?: string;
  coverPanel?: 'front' | 'back' | 'spine';
  element: PageElement;
}> {
  const items: Array<{
    pageId?: string;
    coverPanel?: 'front' | 'back' | 'spine';
    element: PageElement;
  }> = [];
  for (const page of project.pages) {
    for (const el of page.elements) {
      items.push({ pageId: page.id, element: el });
    }
  }
  for (const panel of ['front', 'back', 'spine'] as const) {
    for (const el of project.cover.panels[panel].elements) {
      items.push({ coverPanel: panel, element: el });
    }
  }
  return items;
}

export function runPreflight(project: PhotobookProject): PreflightIssue[] {
  const profile = getProductProfile(project.productProfileId);
  const issues: PreflightIssue[] = [];
  const assetIds = new Set(project.assets.map((a) => a.id));

  const { min, max, step } = profile.pageCount;
  if (
    project.pages.length < min ||
    project.pages.length > max ||
    (project.pages.length - min) % step !== 0
  ) {
    issues.push({
      id: 'page-count',
      code: 'INVALID_PAGE_COUNT',
      severity: 'blocking',
      message: `Het pagina-aantal (${project.pages.length}) voldoet niet aan de productregels (${min}–${max}, stap ${step}).`,
    });
  }

  const orders = project.pages.map((p) => p.order).sort((a, b) => a - b);
  for (let i = 0; i < orders.length; i++) {
    if (orders[i] !== i) {
      issues.push({
        id: 'page-order',
        code: 'INVALID_PAGE_ORDER',
        severity: 'error',
        message: 'De paginavolgorde is ongeldig.',
      });
      break;
    }
  }

  for (const item of collectElements(project)) {
    const el = item.element;
    if (el.type === 'photoFrame') {
      const frame = el as PhotoFrameElement;
      if (frame.required && !frame.assetId) {
        issues.push({
          id: `empty-${frame.id}`,
          code: 'EMPTY_PHOTO_FRAME',
          severity: 'warning',
          message: 'Een verplicht fotokader is nog leeg.',
          pageId: item.pageId,
          coverPanel: item.coverPanel,
          elementId: frame.id,
        });
      }
      if (frame.assetId && !assetIds.has(frame.assetId)) {
        issues.push({
          id: `missing-${frame.id}`,
          code: 'MISSING_ASSET',
          severity: 'blocking',
          message: 'Een foto ontbreekt in de bibliotheek.',
          pageId: item.pageId,
          coverPanel: item.coverPanel,
          elementId: frame.id,
        });
      }
      if (frame.assetId) {
        const asset = project.assets.find((a) => a.id === frame.assetId);
        if (asset) {
          const dpi = calculateEffectiveDpi({
            sourceWidthPx: asset.widthPx,
            sourceHeightPx: asset.heightPx,
            frameWidthMm: frame.width,
            frameHeightMm: frame.height,
            placementScale: frame.placement.scale,
          });
          const status = dpiStatus(dpi, profile);
          if (status === 'critical') {
            issues.push({
              id: `dpi-crit-${frame.id}`,
              code: 'DPI_CRITICAL',
              severity: project.settings.allowLowDpiLocalExport ? 'warning' : 'blocking',
              message: `Fotoresolutie te laag (~${Math.round(dpi)} dpi). Minimum is ${profile.minDpi} dpi.`,
              pageId: item.pageId,
              coverPanel: item.coverPanel,
              elementId: frame.id,
            });
          } else if (status === 'low' || status === 'warn') {
            issues.push({
              id: `dpi-warn-${frame.id}`,
              code: 'DPI_LOW',
              severity: 'warning',
              message: `Fotoresolutie onder het streefniveau (~${Math.round(dpi)} dpi, doel ${profile.targetDpi}).`,
              pageId: item.pageId,
              coverPanel: item.coverPanel,
              elementId: frame.id,
            });
          }
          if (asset.syncStatus === 'error' || asset.syncStatus === 'pending-upload') {
            if (project.ownerMode === 'cloud') {
              issues.push({
                id: `asset-sync-${frame.id}`,
                code: 'ASSET_NOT_READY',
                severity: 'error',
                message: 'Een foto is nog niet volledig beschikbaar in de cloud.',
                pageId: item.pageId,
                coverPanel: item.coverPanel,
                elementId: frame.id,
              });
            }
          }
        }
      }
    }
    if (el.type === 'text') {
      const profileSafe = profile.safeMarginMm;
      const pageW = item.coverPanel === 'spine' ? 20 : profile.pageSizeMm.width;
      const pageH = profile.pageSizeMm.height;
      if (
        el.x < profileSafe ||
        el.y < profileSafe ||
        el.x + el.width > pageW - profileSafe ||
        el.y + el.height > pageH - profileSafe
      ) {
        issues.push({
          id: `safe-${el.id}`,
          code: 'TEXT_OUTSIDE_SAFE_ZONE',
          severity: 'warning',
          message: 'Tekst valt buiten de veilige zone.',
          pageId: item.pageId,
          coverPanel: item.coverPanel,
          elementId: el.id,
        });
      }
    }
  }

  return issues;
}
